import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Send, List, Square } from 'lucide-react';
import { useAgentChat } from '../../contexts/AgentChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { PROVIDER_LABELS } from '../../types/agent.types';
import skillService from '../../services/skill.service';
import AgentWelcome from './AgentWelcome';
import AgentMessageComponent from './AgentMessage';
import AgentTypingIndicator from './AgentTypingIndicator';
import ConfirmationCard from './ConfirmationCard';
import AgentConversationList from './AgentConversationList';
import SlashCommandsDropdown, { type SlashCommand } from './SlashCommandsDropdown';

const AgentChatContent: React.FC = () => {
  const {
    conversationId,
    conversationStatus,
    currentProvider,
    messages,
    isLoading,
    loadingElapsed,
    pendingConfirmation,
    lastConfirmResult,
    sendMessage,
    confirmAction,
    cancelAction,
    startNewConversation,
    endConversation,
    agentView,
    setAgentView,
  } = useAgentChat();

  const [text, setText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [slashCommands, setSlashCommands] = useState<SlashCommand[]>([]);
  const [slashLoading, setSlashLoading] = useState(false);
  const [showSlashDropdown, setShowSlashDropdown] = useState(false);
  const [slashHighlightedIndex, setSlashHighlightedIndex] = useState(0);
  const slashLoadedRef = useRef(false);
  const slashFetchingRef = useRef(false);

  // Whitelist /commands зависит от ролей в активной организации.
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.id ?? null;
  useEffect(() => {
    if (!slashLoadedRef.current) return;
    slashLoadedRef.current = false;
    setSlashCommands([]);
  }, [orgId]);

  const loadSlashCommands = useCallback(async () => {
    if (slashLoadedRef.current || slashFetchingRef.current) return;
    slashFetchingRef.current = true;
    setSlashLoading(true);
    try {
      const resp = await skillService.getCommands();
      setSlashCommands(resp.commands ?? []);
      slashLoadedRef.current = true;
    } catch {
      setSlashCommands([]);
    } finally {
      setSlashLoading(false);
      slashFetchingRef.current = false;
    }
  }, []);

  const filteredSlashCommands = useMemo(
    () => showSlashDropdown
      ? slashCommands.filter(cmd => {
          const query = text.toLowerCase();
          return cmd.command.toLowerCase().startsWith(query) || cmd.name.toLowerCase().includes(query.slice(1));
        })
      : [],
    [showSlashDropdown, slashCommands, text],
  );

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading, pendingConfirmation]);

  const handleSend = useCallback(async () => {
    if (!text.trim() || isLoading) return;

    const content = text.trim();
    setText('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    let convId = conversationId;
    if (!convId) {
      convId = await startNewConversation();
      if (!convId) return;
    }

    await sendMessage(content, convId);
  }, [text, isLoading, conversationId, startNewConversation, sendMessage]);

  const handleQuickAction = useCallback(async (message: string) => {
    let convId = conversationId;
    if (!convId) {
      convId = await startNewConversation();
      if (!convId) return;
    }
    await sendMessage(message, convId);
  }, [conversationId, startNewConversation, sendMessage]);

  const handleSlashSelect = useCallback((cmd: SlashCommand) => {
    setText(cmd.command + ' ');
    setShowSlashDropdown(false);
    textareaRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Slash dropdown navigation
    if (showSlashDropdown && filteredSlashCommands.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSlashHighlightedIndex(i => (i + 1) % filteredSlashCommands.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSlashHighlightedIndex(i => (i - 1 + filteredSlashCommands.length) % filteredSlashCommands.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        const cmd = filteredSlashCommands[slashHighlightedIndex];
        if (!cmd) return;
        e.preventDefault();
        handleSlashSelect(cmd);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowSlashDropdown(false);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend, showSlashDropdown, filteredSlashCommands, slashHighlightedIndex, handleSlashSelect]);

  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setText(val);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 96) + 'px';

    if (val.startsWith('/') && !val.includes(' ')) {
      loadSlashCommands();
      setShowSlashDropdown(true);
      setSlashHighlightedIndex(0);
    } else {
      setShowSlashDropdown(false);
    }
  }, [loadSlashCommands]);

  // Conversation list view
  if (agentView === 'list') {
    return <AgentConversationList />;
  }

  const providerInfo = currentProvider ? PROVIDER_LABELS[currentProvider] : null;
  const canSend = text.trim().length > 0 && !isLoading;
  const showWelcome = messages.length === 0 && !isLoading;

  return (
    <>
      {/* Sub-nav */}
      <div className="flex items-center justify-between flex-shrink-0" style={{ padding: 'var(--space-2) var(--space-4) 0' }}>
        <div>
          {conversationId && conversationStatus === 'active' && (
            <button
              type="button"
              onClick={endConversation}
              className="flex items-center gap-1 bg-error-50 border-error-200 rounded-md cursor-pointer text-xs text-error-600" style={{ padding: '2px var(--space-2)', fontFamily: 'inherit' }}
            >
              <Square size={10} />
              Завершить
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => setAgentView('list')}
          className="flex items-center gap-1 bg-gray-100 border-none rounded-md cursor-pointer text-xs text-gray-500" style={{ padding: '2px var(--space-2)', fontFamily: 'inherit' }}
        >
          <List size={12} />
          Беседы
        </button>
      </div>

      {/* Provider badge */}
      {providerInfo && (
        <div style={{
          padding: '0 var(--space-4) var(--space-1)',
          flexShrink: 0,
        }}>
          <span className="d-inline-flex items-center gap-1 px-2-py-1 bg-gray-100 rounded-full text-xs text-gray-600 font-medium">
            {providerInfo.label}
          </span>
        </div>
      )}

      {/* Messages area */}
      <div
        className="flex-1 overflow-y-auto flex flex-col-dir"
        aria-live="polite"
      >
        {showWelcome ? (
          <AgentWelcome onTopicClick={handleQuickAction} />
        ) : (
          <div className="flex flex-col-dir gap-3 py-4">
            {messages.map(msg => (
              <AgentMessageComponent key={msg.id} message={msg} />
            ))}

            {/* Confirmation card */}
            {pendingConfirmation && (
              <ConfirmationCard
                preview={pendingConfirmation}
                onConfirm={confirmAction}
                onCancel={cancelAction}
                confirmResult={lastConfirmResult}
              />
            )}

            {/* Success card (after confirm, no more pending) */}
            {!pendingConfirmation && lastConfirmResult?.success && (
              <ConfirmationCard
                preview=""
                onConfirm={confirmAction}
                onCancel={cancelAction}
                confirmResult={lastConfirmResult}
              />
            )}

            {isLoading && <AgentTypingIndicator elapsed={loadingElapsed} />}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input or ended state */}
      {conversationStatus === 'completed' || conversationStatus === 'cancelled' ? (
        <div className="border-t-gray-200 px-4-py-3 flex flex-col-dir items-center gap-2 flex-shrink-0">
          <span className="text-gray-500 text-xs">
            Диалог завершён
          </span>
          <button
            type="button"
            onClick={startNewConversation}
            className="btn-primary w-full justify-center text-sm"
          >
            Новый диалог
          </button>
        </div>
      ) : (
      <div className="border-t-gray-200 p-3 bg-surface-1 flex-shrink-0 relative">
        {showSlashDropdown && (
          <SlashCommandsDropdown
            commands={filteredSlashCommands}
            loading={slashLoading}
            highlightedIndex={slashHighlightedIndex}
            onSelect={handleSlashSelect}
            onHighlight={setSlashHighlightedIndex}
          />
        )}
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder="Опишите что нужно создать..."
            disabled={isLoading || !!pendingConfirmation}
            maxLength={5000}
            rows={1}
            className="flex-1 border-gray-200 rounded-md text-sm outline-none overflow-y-auto" style={{ resize: 'none', padding: 'var(--space-2) var(--space-3)', lineHeight: 'var(--leading-normal)', fontFamily: 'inherit' }}
            aria-label="Сообщение для AI агента"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend}
            style={{
              background: canSend ? 'var(--primary-600)' : 'var(--gray-200)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              color: canSend ? 'var(--white)' : 'var(--gray-400)',
              cursor: canSend ? 'pointer' : 'not-allowed',
              padding: 'var(--space-2)',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              transition: 'background 0.15s',
            }}
            aria-label="Отправить"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
      )}
    </>
  );
};

export default AgentChatContent;
